import React, { useState, useEffect } from "react";
import {
  Text,
  Button,
  LoadingOverlay,
  Flex,
  Box,
  ThemeIcon,
  Modal,
  Badge,
  Loader,
  Group,
  ActionIcon,
  UnstyledButton,
  Stack,
} from "@mantine/core";
import axios from "axios";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  IconCheck,
  IconUser,
  IconRefresh,
  IconKey,
  IconMapPin,
  IconMinus,
  IconPlus,
} from "@tabler/icons-react";
import toast from "react-hot-toast";
import { numberFormatter } from "@util/formatters";
import { EffortInfo, GroupsWrapper, GroupWrapper } from "./";
import { getEffortUserGroups } from "./helpers";
import { Item as UserGroupItem } from "@components/UserGroup/UserGroupList";
import entityIcon from "@util/entityIcon";
import entityColor from "@util/entityColor";

const UserGroup = ({
  title,
  dateText,
  description,
  added,
  showRemove = true,
  userCount,
  reqData = {},
  fetchData,
  onCreateSuccess,
  item, // the raw item
  onUpdateSuccess,
  creatingUserGroup = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [groupData, setGroupData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [addingUsers, setAddingUsers] = useState(false);
  const [removingUsers, setRemovingUsers] = useState(false);
  const [showEffortInfo, setShowEffortInfo] = useState(false);
  const iconProps = {
    size: "xs",
    radius: "xl",
  };

  useEffect(() => {
    if (!isOpen) return;

    fetchGroupData();
  }, [isOpen]);

  function fetchGroupData() {
    setLoading(true);

    const req = {
      ...reqData,
    };

    if (added) req.removing_users = true;

    axios
      .post(`/bulk-communicators/user-group-check/`, req)
      .then(({ data }) => {
        setGroupData(data.response[0]);
      })
      .catch((err) => {
        setGroupData(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  function onClose() {
    if (loading || addingUsers) return;

    setIsOpen(false);
    setGroupData(null);
  }

  function onAddClick() {
    setAddingUsers(true);

    const req = {
      ...reqData,
    };

    setAddingUsers(true);

    axios
      .post(`/bulk-communicators/add-user-group/`, req)
      .then(({ data }) => {
        onClose();
        fetchData();
      })
      .catch((err) => {
        toast.error(err);
      })
      .finally(() => {
        setAddingUsers(false);
      });
  }

  function onRemoveClick() {
    const req = {
      ...reqData,
      removing_users: true,
    };

    setRemovingUsers(true);

    axios
      .post(`/bulk-communicators/remove-user-group/`, req)
      .then(({ data }) => {
        onClose();
        fetchData();
      })
      .catch((err) => {
        toast.error(err);
      })
      .finally(() => {
        setRemovingUsers(false);
      });
  }

  function evaluateAddClick() {
    if (!item.user_group_id) {
      createEffortUserGroup();
    } else {
      // onAddClick();
      setIsOpen(true);
    }
  }

  function createEffortUserGroup() {
    setLoading(true);
    axios
      .post(`/segmentation/add-effort-user-groups/`, {
        ...reqData,
        campaign_effort_id: reqData.campaign_effort_id,
        variety: reqData.variety,
      })
      .then(({ data }) => {
        if (onCreateSuccess) {
          onCreateSuccess();
        } else {
          fetchData();
        }
      })
      .catch((err) => {
        toast.error(err);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  const stats = groupData
    ? added
      ? [
          {
            label: "Users already added",
            value: numberFormatter(groupData.users_already_added_count),
          },
          {
            label: "Users to remove",
            value: numberFormatter(groupData.users_to_remove_count),
          },
          {
            label: "User Remaining",
            value: numberFormatter(groupData.users_remaining_count),
          },
        ]
      : [
          {
            label: "Total Users",
            value: numberFormatter(groupData.total_snapshot_user_count),
          },
          {
            label: "Users opted-in for marketing",
            value: numberFormatter(groupData.opted_in_user_count),
          },
          {
            label: "Users already added to Audience",
            value: numberFormatter(groupData.users_already_added_count),
          },
          {
            label: "Remaining users to add",
            value: numberFormatter(groupData.net_users_to_add_count),
          },
        ]
    : [];

  const hasUsersToAdd = groupData?.net_users_to_add_count > 0;
  const showButtons = added || hasUsersToAdd;

  if (item.report_snapshot_id) {
    return (
      <>
        <UserGroupItem
          original={{
            ...item,
            can_edit: creatingUserGroup ? true : false,
          }}
          fetchData={fetchData}
          entityData={reqData}
        >
          {/* {children} */}
          {item.user_count > 0 && (
            <AddRemoveButtons
              reqData={{
                ...reqData,
                user_group_id: item.user_group_id,
                variety: item.variety,
              }}
              title={item.title}
              fetchData={() => {
                fetchData();
              }}
              added={false}
            />
          )}
        </UserGroupItem>
      </>
    );
  }

  return (
    <div style={{ position: "relative" }}>
      <Flex
        align={{ base: "flex-start", md: "center" }}
        gap="lg"
        direction={{ base: "column", sm: "row" }}
      >
        <Box>
          <ThemeIcon
            color={entityColor.effort}
            variant="light"
            size="lg"
            radius="xl"
          >
            {entityIcon.effort(16)}
          </ThemeIcon>
        </Box>
        <div style={{ flexGrow: 1 }}>
          <Stack gap={1}>
            <Flex gap="xs" align="center">
              <Text size={creatingUserGroup ? "lg" : "md"} fw={600}>
                {item.internal_name || title}
              </Text>
            </Flex>
            {typeof userCount === "number" && (
              <div>
                <Badge
                  size={creatingUserGroup ? "lg" : "md"}
                  color={added ? "dark.4" : "gray"}
                  variant={added ? "filled" : "light"}
                  leftSection={<IconUser size={12} />}
                >
                  {numberFormatter(userCount)}
                </Badge>
              </div>
            )}
            {item.internal_name && item.title && (
              <Text c="dimmed" size="sm">
                {item.title}
              </Text>
            )}
            {description && (
              <Text size={creatingUserGroup ? "sm" : "xs"} maw={"70%"}>
                {creatingUserGroup && !item.id
                  ? "ready for create"
                  : description}
              </Text>
            )}
            {dateText && (
              <Text size={creatingUserGroup ? "sm" : "xs"}>{dateText}</Text>
            )}
          </Stack>
        </div>
        <Flex gap="xs">
          {added ? (
            <>
              {showRemove ? (
                <>
                  <Button
                    variant="white"
                    color="red"
                    size="xs"
                    onClick={() => setIsOpen(true)}
                  >
                    Remove
                  </Button>
                  {/* <ActionIcon
                  {...iconProps}
                  variant="light"
                  color={"red"}
                  onClick={() => setIsOpen(true)}
                >
                  <IconMinus size={12} />
                </ActionIcon> */}
                </>
              ) : (
                <>
                  <ThemeIcon {...iconProps} color={"green"} variant="outline">
                    <IconCheck size={12} />
                  </ThemeIcon>
                </>
              )}
            </>
          ) : (
            <>
              {item.updateable && !creatingUserGroup && (
                <UpdateButton
                  item={item}
                  fetchData={() => {
                    if (onUpdateSuccess) {
                      onUpdateSuccess();
                    } else {
                      fetchData();
                    }
                  }}
                />
              )}
              {item.is_effort ? (
                <>
                  <Button
                    variant="light"
                    color={entityColor.effort}
                    size="xs"
                    radius="xl"
                    onClick={() => setShowEffortInfo(!showEffortInfo)}
                  >
                    view segments
                  </Button>
                </>
              ) : (
                <>
                  {item.id && creatingUserGroup ? (
                    <Flex align="center" gap="xs">
                      <ThemeIcon
                        {...iconProps}
                        color={"green"}
                        variant="outline"
                      >
                        <IconCheck size={12} />
                      </ThemeIcon>
                      <Text>Segment created</Text>
                    </Flex>
                  ) : (
                    <>
                      <Button
                        // variant="light"
                        // variant={item.id ? "filled" : "light"}
                        color={
                          item.id
                            ? "green"
                            : creatingUserGroup
                            ? "teal"
                            : "blue"
                        }
                        size={creatingUserGroup ? "sm" : "xs"}
                        onClick={evaluateAddClick}
                        loading={loading}
                      >
                        {item.id ? "Add Segment" : "Create Segment"}
                      </Button>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </Flex>
      </Flex>
      {showEffortInfo && (
        <EffortContainer
          title={item.title}
          id={item.id}
          fetchData={fetchData}
          reqData={reqData}
          effortInfo={item}
        />
      )}
      <Modal opened={isOpen} onClose={onClose} title={title}>
        <Box mih={100} style={{ flexGrow: 1, position: "relative" }}>
          <LoadingOverlay visible={loading} />
          {groupData && !loading && (
            <>
              {stats.map((stat, i) => (
                <Flex key={i} align="center" gap="xs">
                  <Text style={{ flexGrow: 1 }} size="md" fw={600}>
                    {stat.label}
                  </Text>
                  <Text size="md" fw={600}>
                    {stat.value}
                  </Text>
                </Flex>
              ))}
              {!added && !hasUsersToAdd && (
                <Text mt="lg" align="center" c="red" size="md" fw={600}>
                  No users to add at this time
                </Text>
              )}
              {showButtons && (
                <>
                  {added ? (
                    <Button
                      fullWidth
                      mt="md"
                      size="md"
                      onClick={onRemoveClick}
                      loading={removingUsers}
                      color="red"
                    >
                      Remove Users
                    </Button>
                  ) : (
                    <Button
                      fullWidth
                      mt="md"
                      size="md"
                      onClick={onAddClick}
                      loading={addingUsers}
                    >
                      Add Users
                    </Button>
                  )}
                </>
              )}
            </>
          )}
        </Box>
      </Modal>
    </div>
  );
};

export default UserGroup;

export function UpdateButton({ item, fetchData }) {
  const [loading, setLoading] = useState(false);

  function onUpdateClick() {
    setLoading(true);
    axios
      .post(`/user-groups/update-user-data/`, {
        user_group_id: item.user_group_id,
      })
      .then(({ data }) => {
        fetchData();
      })
      .catch((err) => {
        toast.error(err);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  return (
    <ActionIcon
      color="gray"
      variant="light"
      size="xs"
      onClick={onUpdateClick}
      loading={loading}
      title="Update users in this segment"
      radius="xl"
    >
      <IconRefresh size={10} />
    </ActionIcon>
  );
}

export const AddRemoveButtons = ({
  reqData,
  fetchData,
  onClose,
  title,
  added,
}) => {
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [groupData, setGroupData] = useState(null);
  const [addingUsers, setAddingUsers] = useState(false);
  const [removingUsers, setRemovingUsers] = useState(false);

  const hasUsersToAdd = groupData?.net_users_to_add_count > 0;
  const showButtons = added || hasUsersToAdd;

  useEffect(() => {
    if (!isOpen) return;

    fetchGroupData();
  }, [isOpen]);

  function fetchGroupData() {
    setLoading(true);

    const req = {
      ...reqData,
    };

    if (added) req.removing_users = true;

    axios
      .post(`/bulk-communicators/user-group-check/`, req)
      .then(({ data }) => {
        setGroupData(data.response[0]);
      })
      .catch((err) => {
        setGroupData(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  function onClose() {
    if (loading || addingUsers) return;

    setIsOpen(false);
    setGroupData(null);
  }

  function onAddClick() {
    setAddingUsers(true);

    const req = {
      ...reqData,
    };

    setAddingUsers(true);

    axios
      .post(`/bulk-communicators/add-user-group/`, req)
      .then(({ data }) => {
        onClose();
        fetchData();
      })
      .catch((err) => {
        toast.error(err);
      })
      .finally(() => {
        setAddingUsers(false);
      });
  }

  function onRemoveClick() {
    const req = {
      ...reqData,
      removing_users: true,
    };

    setRemovingUsers(true);

    axios
      .post(`/bulk-communicators/remove-user-group/`, req)
      .then(({ data }) => {
        onClose();
        fetchData();
      })
      .catch((err) => {
        toast.error(err);
      })
      .finally(() => {
        setRemovingUsers(false);
      });
  }

  const stats = groupData
    ? added
      ? [
          {
            label: "Users already added",
            value: numberFormatter(groupData.users_already_added_count),
          },
          {
            label: "Users to remove",
            value: numberFormatter(groupData.users_to_remove_count),
          },
          {
            label: "User Remaining",
            value: numberFormatter(groupData.users_remaining_count),
          },
        ]
      : [
          {
            label: "Total Users",
            value: numberFormatter(groupData.total_snapshot_user_count),
          },
          {
            label: "Users opted-in for marketing",
            value: numberFormatter(groupData.opted_in_user_count),
          },
          {
            label: "Users already added to Audience",
            value: numberFormatter(groupData.users_already_added_count),
          },
          {
            label: "Remaining users to add",
            value: numberFormatter(groupData.net_users_to_add_count),
          },
        ]
    : [];
  return (
    <>
      {added ? (
        <ActionIcon
          color="red"
          variant="light"
          size="xs"
          onClick={() => setIsOpen(true)}
          loading={loading}
          title="Remove Segment"
          radius="xl"
        >
          <IconMinus size={14} />
        </ActionIcon>
      ) : (
        <ActionIcon
          color="green"
          size="xs"
          onClick={() => setIsOpen(true)}
          loading={loading}
          title="Add Segment"
          radius="xl"
        >
          <IconPlus size={14} />
        </ActionIcon>
      )}
      <Modal opened={isOpen} onClose={onClose} title={title}>
        <Box mih={100} style={{ flexGrow: 1, position: "relative" }}>
          <LoadingOverlay visible={loading} />
          {groupData && !loading && (
            <>
              {stats.map((stat, i) => (
                <Flex key={i} align="center" gap="xs">
                  <Text style={{ flexGrow: 1 }} size="md" fw={600}>
                    {stat.label}
                  </Text>
                  <Text size="md" fw={600}>
                    {stat.value}
                  </Text>
                </Flex>
              ))}
              {!added && !hasUsersToAdd && (
                <Text mt="lg" align="center" c="red" size="md" fw={600}>
                  No users to add at this time
                </Text>
              )}
              {showButtons && (
                <>
                  {added ? (
                    <Button
                      fullWidth
                      mt="md"
                      size="md"
                      onClick={onRemoveClick}
                      loading={removingUsers}
                      color="red"
                    >
                      Remove Users
                    </Button>
                  ) : (
                    <Button
                      fullWidth
                      mt="md"
                      size="md"
                      onClick={onAddClick}
                      loading={addingUsers}
                    >
                      Add Users
                    </Button>
                  )}
                </>
              )}
            </>
          )}
        </Box>
      </Modal>
    </>
  );
};

export const EffortContainer = ({
  title,
  id,
  fetchData,
  reqData,
  effortInfo,
  creatingUserGroup = false,
}) => {
  const {
    data: effortData,
    isLoading: loading,
    error,
  } = useQuery({
    queryKey: ["available-user-group-effort-data", id],
    queryFn: async () => {
      try {
        const { data } = await axios.post(
          `/segmentation/retrieve-effort-user-groups/`,
          {
            campaign_effort_id: id,
          }
        );
        return data.response[0];
      } catch (error) {
        throw error;
      }
    },
    retry: 1, // Only retry once
    retryDelay: 1000, // Wait 1 second before retry
  });
  const queryClient = useQueryClient();

  if (loading) return <Loader size="xs" />;
  if (error)
    return (
      <Text size="sm" c="red" fw={500}>
        {error}
      </Text>
    );
  if (!effortData) return <></>;

  const userGroups = getEffortUserGroups(effortData);
  const fetchEffortData = () =>
    queryClient.invalidateQueries({
      queryKey: ["available-user-group-effort-data", id],
    });
  const counts = {
    added: userGroups.filter((m) => m.user_id_count).length,
    toAdd: userGroups.filter((m) => !m.user_id_count).length,
  };
  const effortUserGroupData = {
    added: userGroups.filter((f) => f.id),
    available: userGroups.filter((f) => !f.id),
  };

  return (
    <Box mt={creatingUserGroup ? "0" : "sm"}>
      {effortUserGroupData.added.length > 0 && (
        <Box mb="md">
          <EffortInfo
            effort={{
              ...effortData,
              title: `Created segments from ${
                effortInfo ? effortInfo.internal_name : "this effort"
              }`,
            }}
          />
          <Stack
            gap="xs"
            style={{
              border: "1px solid var(--mantine-color-gray-3)",
              padding: "var(--mantine-spacing-xs)",
              backgroundColor: "var(--mantine-color-gray-0)",
            }}
          >
            {effortUserGroupData.added.map((group, i) => (
              <div key={group.id}>
                {/* <UserGroup
                  title={group.title}
                  description={group.description}
                  added={false}
                  userCount={group.user_id_count}
                  creatingUserGroup={creatingUserGroup}
                  item={group}
                  reqData={{
                    ...reqData,
                    user_group_id: group.user_group_id,
                    campaign_effort_id: id,
                    variety: group.variety,
                  }}
                  fetchData={() => {
                    fetchData();
                  }}
                  onCreateSuccess={() => {
                    fetchEffortData();
                  }}
                  onUpdateSuccess={() => {
                    fetchEffortData();
                  }}
                /> */}
                <UserGroupItem
                  original={{
                    ...group,
                    description: group.description,
                    can_edit: creatingUserGroup ? true : false,
                  }}
                  fetchData={fetchEffortData}
                  entityData={{ campaign_effort_id: id }}
                >
                  {group.user_count > 0 && !creatingUserGroup && (
                    <AddRemoveButtons
                      reqData={{
                        ...reqData,
                        user_group_id: group.user_group_id,
                        campaign_effort_id: id,
                        variety: group.variety,
                      }}
                      fetchData={() => {
                        // fetchEffortData();
                        fetchData();
                      }}
                      added={false}
                    />
                  )}
                </UserGroupItem>
              </div>
            ))}
          </Stack>
        </Box>
      )}
      {effortUserGroupData.available.length > 0 && (
        <>
          <EffortInfo
            effort={{
              ...effortData,
              title: creatingUserGroup
                ? "Create a new segment"
                : `Create a new segment from ${
                    effortInfo ? effortInfo.internal_name : "this effort"
                  }`,
            }}
          />
          <GroupsWrapper connected visible added={false}>
            {effortUserGroupData.available.map((group, i) => (
              <GroupWrapper key={i} added={false}>
                <UserGroup
                  title={group.title}
                  description={group.description}
                  added={false}
                  userCount={group.user_id_count}
                  creatingUserGroup={creatingUserGroup}
                  item={group}
                  reqData={{
                    ...reqData,
                    user_group_id: group.user_group_id,
                    campaign_effort_id: id,
                    variety: group.variety,
                  }}
                  fetchData={() => {
                    fetchData();
                  }}
                  onCreateSuccess={() => {
                    fetchEffortData();
                  }}
                  onUpdateSuccess={() => {
                    fetchEffortData();
                  }}
                />
              </GroupWrapper>
            ))}
          </GroupsWrapper>
        </>
      )}
    </Box>
  );
};
